import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Sparkles, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const year = new Date().getFullYear();

  return (
    <footer className="aurora-footer" data-testid="site-footer">
      <div className="aurora-footer-glow aurora-footer-glow--a" aria-hidden="true" />
      <div className="aurora-footer-glow aurora-footer-glow--b" aria-hidden="true" />
      <div className="aurora-footer-divider" aria-hidden="true" />

      <div className="aurora-footer-inner">
        {/* Brand column */}
        <div className="aurora-footer-brand">
          <button
            type="button"
            onClick={scrollToTop}
            className="aurora-footer-logo"
            aria-label="Nach oben scrollen"
            data-testid="footer-logo"
          >
            <span className="aurora-footer-logo-icon">
              <Sparkles size={18} strokeWidth={2.4} />
            </span>
            <span className="aurora-footer-logo-text">
              MPV <strong>SOLUTIONS</strong>
            </span>
          </button>
          <p className="aurora-footer-tagline">
            Wir digitalisieren Versicherungsagenturen mit KI, WhatsApp und einem CRM,
            das wirklich funktioniert.
          </p>
        </div>

        {/* Navigation */}
        <div className="aurora-footer-column">
          <h4 className="aurora-footer-heading">Navigation</h4>
          <ul className="aurora-footer-links">
            <li><Link to="/" data-testid="footer-link-home">Startseite</Link></li>
            <li><Link to="/leistungen" data-testid="footer-link-leistungen">Leistungen</Link></li>
            <li><Link to="/ki-academy" data-testid="footer-link-academy">KI Academy</Link></li>
            <li><Link to="/tools" data-testid="footer-link-tools">Tools</Link></li>
          </ul>
        </div>

        {/* Legal */}
        <div className="aurora-footer-column">
          <h4 className="aurora-footer-heading">Rechtliches</h4>
          <ul className="aurora-footer-links">
            <li><Link to="/impressum" data-testid="footer-link-impressum">Impressum</Link></li>
            <li><Link to="/datenschutz" data-testid="footer-link-datenschutz">Datenschutz</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="aurora-footer-column">
          <h4 className="aurora-footer-heading">Kontakt</h4>
          <ul className="aurora-footer-links aurora-footer-contact">
            <li>
              <Mail size={14} strokeWidth={2.2} />
              <a href="mailto:info@mpv-solutions.com" data-testid="footer-email">
                info@mpv-solutions.com
              </a>
            </li>
            <li>
              <Phone size={14} strokeWidth={2.2} />
              <a href="tel:+4917197790880" data-testid="footer-phone">
                +49 171 977 90 80
              </a>
            </li>
            <li>
              <MapPin size={14} strokeWidth={2.2} />
              <span>Bahnhofstraße 33 · 42651 Solingen</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="aurora-footer-bottom">
        <p className="aurora-footer-copy">
          © {year} MPV SOLUTIONS. Alle Rechte vorbehalten.
        </p>
        <button
          type="button"
          onClick={scrollToTop}
          className="aurora-footer-top"
          aria-label="Nach oben"
          data-testid="footer-scroll-top"
        >
          <ArrowUp size={14} strokeWidth={2.4} />
          <span>Nach oben</span>
        </button>
      </div>

      <style>{`
        .aurora-footer {
          position: relative;
          margin-top: 60px;
          padding: 64px 24px 32px;
          background:
            linear-gradient(180deg, rgba(6, 8, 16, 0.55), rgba(4, 6, 14, 0.9)) padding-box;
          color: #F5F7FA;
          overflow: hidden;
          isolation: isolate;
        }
        .aurora-footer::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(900px 400px at 15% 0%, rgba(0, 255, 209, 0.10), transparent 60%),
            radial-gradient(900px 400px at 85% 100%, rgba(168, 85, 247, 0.10), transparent 60%);
          pointer-events: none;
          z-index: -1;
        }
        .aurora-footer-divider {
          position: absolute; top: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg,
            transparent, rgba(0, 255, 209, 0.4) 25%,
            rgba(168, 85, 247, 0.4) 75%, transparent);
          box-shadow: 0 0 24px rgba(0, 255, 209, 0.25);
        }
        .aurora-footer-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
          z-index: -1;
        }
        .aurora-footer-glow--a {
          top: -100px; left: -80px;
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(0, 255, 209, 0.16), transparent 70%);
        }
        .aurora-footer-glow--b {
          bottom: -140px; right: -80px;
          width: 460px; height: 460px;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.16), transparent 70%);
        }

        .aurora-footer-inner {
          position: relative;
          max-width: 1240px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.6fr 1fr 1fr 1.2fr;
          gap: 44px;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        /* Brand */
        .aurora-footer-logo {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 6px 10px 6px 4px;
          border-radius: 12px;
          transition: background .25s ease;
          color: #FFFFFF;
        }
        .aurora-footer-logo:hover { background: rgba(255,255,255,0.04); }
        .aurora-footer-logo-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px; height: 34px;
          border-radius: 10px;
          background: linear-gradient(135deg, #00FFD1, #A855F7);
          color: #061014;
          box-shadow: 0 0 20px rgba(0, 255, 209, 0.45);
        }
        .aurora-footer-logo-text {
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.02em;
          color: rgba(255,255,255,0.85);
        }
        .aurora-footer-logo-text strong {
          background: linear-gradient(135deg, #00FFD1, #00D4FF);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
        }
        .aurora-footer-tagline {
          margin: 14px 0 0;
          font-size: 13.5px;
          line-height: 1.65;
          color: rgba(255,255,255,0.58);
          max-width: 340px;
        }

        /* Columns */
        .aurora-footer-heading {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #FFFFFF;
          margin: 0 0 18px;
          position: relative;
          padding-bottom: 10px;
        }
        .aurora-footer-heading::after {
          content: '';
          position: absolute;
          left: 0; bottom: 0;
          width: 28px; height: 2px;
          border-radius: 2px;
          background: linear-gradient(90deg, #00FFD1, #A855F7);
          box-shadow: 0 0 10px rgba(0, 255, 209, 0.5);
        }
        .aurora-footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .aurora-footer-links a,
        .aurora-footer-links span {
          font-size: 13.5px;
          color: rgba(255,255,255,0.68);
          text-decoration: none;
          transition: color .3s ease, transform .3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .aurora-footer-links a:hover {
          color: #00FFD1;
          transform: translateX(3px);
        }
        .aurora-footer-contact li {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13.5px;
          color: rgba(255,255,255,0.68);
        }
        .aurora-footer-contact svg { color: #00FFD1; flex-shrink: 0; }

        /* Bottom */
        .aurora-footer-bottom {
          position: relative;
          max-width: 1240px;
          margin: 0 auto;
          padding-top: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
        }
        .aurora-footer-copy {
          margin: 0;
          font-size: 12.5px;
          color: rgba(255,255,255,0.45);
          letter-spacing: 0.02em;
        }
        .aurora-footer-top {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(0, 255, 209, 0.28);
          color: rgba(255,255,255,0.8);
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all .3s ease;
        }
        .aurora-footer-top:hover {
          color: #00FFD1;
          background: rgba(0, 255, 209, 0.06);
          border-color: rgba(0, 255, 209, 0.6);
          box-shadow: 0 0 16px rgba(0, 255, 209, 0.28);
          transform: translateY(-1px);
        }

        @media (max-width: 900px) {
          .aurora-footer-inner { grid-template-columns: 1fr 1fr; gap: 32px; }
        }
        @media (max-width: 560px) {
          .aurora-footer { padding: 48px 18px 24px; }
          .aurora-footer-inner { grid-template-columns: 1fr; gap: 28px; }
          .aurora-footer-bottom { justify-content: center; text-align: center; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
